import { SportType } from '../../court/dto/court.dto';
import { OrganizationLocationDto } from '../../location/dto/location.dto';
import { EventParticipantDto } from './event-participant.dto';
import { EventTeamDto } from './event-team.dto';
import { EventWaitlistEntryDto } from './event-waitlist-entry.dto';

export type EventDto = {
  id: number;
  name: string;
  startTime: string;
  duration: number;
  location: string;
  capacity: number;
  participants: EventParticipantDto[];
  eventType: EventType;
  prizes?: EventPrize[];
  minLevel?: number;
  maxLevel?: number;
  minRanking?: string;
  maxRanking?: string;
  sportType?: SportType;
  organizationLocation: OrganizationLocationDto;
  status: EventStatus;
  recurringGroupId?: string;
  price?: number;
  teamSize?: number;
  teamBased: boolean;
  tournamentCategory?: TournamentCategory;
  genderRestriction?: GenderRestriction;
  imageUrl?: string;
  teams: EventTeamDto[];
  courts: Array<{
    id: number;
    name: string;
  }>;
  cohort?: { id: string; name: string } | null;
  /** Staff member running the event. Null when the event has no host. */
  coach?: EventCoachDto | null;
  sharableLink?: string;
  waitlistEnabled: boolean;
  /**
   * Tournament licence gate. When true, a player without a licence number on
   * their profile is refused registration (409 `LICENSE_NUMBER_REQUIRED`) and
   * the client must collect it before retrying.
   */
  requiresLicenseNumber: boolean;
  maxWaitlistSize?: number;
  waitlistCount: number;
  waitlistEntries?: EventWaitlistEntryDto[];
};

/**
 * Slim projection of the coach hosting an event — just what the player-facing
 * event page renders (name + photo). Deliberately not `CoachDto`: that one
 * carries pricing rules and ratings an event never needs.
 */
export type EventCoachDto = {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string;
};

export enum EventType {
  OFFICIAL_COMPETITION = 'official_competition',
  COMPETITION = 'competition',
  CLASS = 'class',
  OTHER = 'other',
}

export enum EventStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

export enum TournamentCategory {
  // Padel official tournament categories
  P25 = 'P25',
  P50 = 'P50',
  P100 = 'P100',
  P250 = 'P250',
  P500 = 'P500',
  P1000 = 'P1000',
  P2000 = 'P2000',
  // Add more categories for other sports as needed
}

export enum GenderRestriction {
  MEN = 'men',
  WOMEN = 'women',
  NO_RESTRICTION = 'no_restriction',
}

export interface EventPrize {
  place: number; // Represents the placement (1st, 2nd, etc.)
  reward: string;
}
